<template>
  <div>
    <div class="mb-1">
      <pdl-form-group
        type="checkbox"
        :name="checkboxId"
        :multi-items="[
          {
            checked: isAllChecked,
            displayName: $t('text.selectAll'),
            value: checkboxValue,
            name: checkboxId,
          },
        ]"
        @change="checkAllItems"
      />
    </div>

    <pdl-collapse
      :ref="collapseRef"
      icon-active="indeterminate_check_box"
      icon-inactive="add_box"
      icon-position="left"
      align-icon="start"
      qaid="cart-sku-grid-collapse"
    >
      <pdl-collapse-item
        v-for="(item, index) in paginatedData"
        :key="getCollapseItemName(partTypeKey, itemIdKey, index)"
        :name="getCollapseItemName(partTypeKey, itemIdKey, index)"
      >
        <!-- Product Group media card / header -->
        <template #title>
          <!-- Variants -->
          <label v-if="item.groupBy == 'Color'" class="flex grow md:mb-1 mt-25 items-center" qaid="sku-grid.color">
            <div class="mr-1">
              <pdl-swatch :name="getGroupDisplayColor(item)" v-bind="getColorProps(item)" :is-small="true" />
            </div>
            <span class="cursor-pointer text-gray-70 leading-tight">{{ getGroupDisplayColorName(item) }}</span>
          </label>

          <div v-else-if="getGroupingVariant(item)" class="text-secondary">
            {{ getGroupingVariant(item) }}
          </div>
        </template>
        <div class="md:flex">
          <div class="b2b-card md:w-24">
            <div class="b2b-card__img w-24 md:w-auto">
              <img :src="getGroupDisplayImage(item)" class="w-20 h-auto lazyload" :alt="item.itemName" />
            </div>
            <div class="b2b-card__body p-0 pt-1">
              <p
                v-if="item.marketingModelYear && !isProjectOne"
                class="text-md text-gray-80 font-medium m-0"
                qaid="sku-grid.model-year"
              >
                {{ item.marketingModelYear }}
              </p>

              <p class="font-medium text-lg mb-0 mt-25 leading-tight" qaid="sku-grid.item-name">{{ item.itemName }}</p>

              <!-- Variants -->
              <label
                v-if="item.groupBy == 'Color'"
                class="flex grow md:mb-1 mt-25 items-center md:block"
                qaid="sku-grid.color"
              >
                <div class="grow mr-1">
                  <pdl-swatch :name="getGroupDisplayColor(item)" v-bind="getColorProps(item)" :is-small="true" />
                </div>
                <span class="cursor-pointer text-gray-70 leading-tight">{{ getGroupDisplayColorName(item) }}</span>
              </label>

              <div v-else-if="getGroupingVariant(item)" class="text-secondary">{{ getGroupingVariant(item) }}</div>
            </div>
          </div>

          <!-- Tabs (SKU and Apple Labels) -->
          <div class="md:flex-1 md:ml-2">
            <pdl-tabs>
              <pdl-tab-pane :label="$t('text.account.savedLists.sku')">
                <!-- SKU GRID -->
                <cart-product-grid
                  v-if="item.entries.length"
                  :key="getProductGridKey(item, index)"
                  :base-product="item"
                  :base-product-index="index"
                  :part-type="partTypeKey"
                  :is-custom-water-bottle="isCustomWaterBottle"
                  :is-project-one="isProjectOne"
                >
                </cart-product-grid>
              </pdl-tab-pane>

              <pdl-tab-pane v-if="displayAppleLabels(partTypeKey)" :label="$t('appleLabel.title')">
                <pdl-callout v-if="!cartToggle" kind="error">
                  <template #content>
                    {{ $t('text.systemDown.cannotSubmit') }}
                  </template>
                </pdl-callout>
                <apple-label-grid :items="getAppleLabelData(item)" :base-product="item.baseProduct"> </apple-label-grid>
              </pdl-tab-pane>
            </pdl-tabs>
          </div>
        </div>
      </pdl-collapse-item>
    </pdl-collapse>

    <div class="grid grid-cols-12 gap-4 mb-5 mt-2">
      <div class="col-span-12 md:col-span-6 lg:col-span-8">
        <pagination-controls
          v-if="pageCount > 1"
          ref="pagination"
          :page-number="pageNumber"
          :page-count="pageCount"
          @page-change="updatePage"
          @prev-page="prevPage"
          @next-page="nextPage"
        >
        </pagination-controls>
      </div>

      <div class="col-span-12 md:col-span-6 lg:col-span-4">
        <!-- Shipment Summary -->
        <ul v-if="!isConsumerFriendlyMode" class="block-list text-md">
          <li class="block-list__item" :qaid="getPartKeyQAID('subtotal')">
            <span>{{ $t('text.order.subtotal') }}</span>
            <span>{{ cartSummary[partTypeKey].subTotal }}</span>
          </li>
          <li class="block-list__item" :qaid="getPartKeyQAID('promotions')">
            <span>{{ $t('text.promotions') }}</span>
            <span class="text-alert">{{ cartSummary[partTypeKey].promotions }}</span>
          </li>
          <li class="block-list__item text-base font-medium" :qaid="getPartKeyQAID('total')">
            <span v-if="partTypeKey === 'bikes'">{{ $t('myCart.B2B.bikesTotal') }}</span>
            <span v-else-if="partTypeKey === 'parts'">{{ $t('myCart.B2B.partsAccessories.total') }}</span>
            <span v-else-if="partTypeKey === 'customWaterBottles'">{{ $t('checkout.custom.total') }}</span>
            <span v-else-if="partTypeKey === 'projectOneBikes'">{{ $t('checkout.projectOne.total') }}</span>
            <span>{{ cartSummary[partTypeKey].subTotal }}</span>
          </li>
        </ul>

        <div class="flex lg:justify-end mt-2 mb-3">
          <div class="buttons">
            <trek-button
              :id="partTypeKey === 'bikes' ? 'saved-lists-button-bikes' : 'saved-lists-button-accessories'"
              v-tippy="{
                allowHTML: true,
                content: getSavedListsPanelElement,
                interactive: true,
                trigger: 'click',
                popperOptions: {
                  modifiers: [
                    {
                      name: 'flip',
                      enabled: true,
                    },
                  ],
                },
                sticky: true,
              }"
              secondary
              block
              icon="list"
              :disabled="isDisabled || partTypeKey === 'customWaterBottles' || partTypeKey === 'projectOneBikes'"
              :qaid="partTypeKey === 'bikes' ? 'save-all-bikes-to-list' : 'save-all-accessories-to-list'"
              @click="updateSavedList"
            >
              <span v-if="partTypeKey === 'bikes'" :key="selectedProductsBikeCount">{{
                $t('text.account.savedLists.moveLines', [selectedProductsBikeCount])
              }}</span>
              <span v-else-if="partTypeKey === 'parts'">{{
                $t('text.account.savedLists.moveLines', [selectedProductsAccessoriesCount])
              }}</span>
              <span v-else-if="partTypeKey === 'customWaterBottles'">{{
                $t('text.account.savedLists.moveLines', [selectedProductsAccessoriesCount])
              }}</span>
              <span v-else-if="partTypeKey === 'projectOneBikes'">{{
                $t('text.account.savedLists.moveLines', [selectedProductsBikeCount])
              }}</span>
            </trek-button>
            <trek-button
              alert
              block
              icon="delete"
              :disabled="isDisabled"
              :qaid="
                partTypeKey === 'bikes' || partTypeKey === 'projectOneBikes'
                  ? 'delete-all-selected-bikes'
                  : 'delete-all-selected-accessories'
              "
              @click="isDialogVisible = true"
            >
              <span v-if="partTypeKey === 'bikes'">{{
                $t('text.account.savedLists.removeLines', [selectedProductsBikeCount])
              }}</span>
              <span v-else-if="partTypeKey === 'parts'">{{
                $t('text.account.savedLists.removeLines', [selectedProductsAccessoriesCount])
              }}</span>
              <span v-else-if="partTypeKey === 'customWaterBottles'">{{
                $t('text.account.savedLists.removeLines', [selectedProductsAccessoriesCount])
              }}</span>
              <span v-else-if="partTypeKey === 'projectOneBikes'">{{
                $t('text.account.savedLists.removeLines', [selectedProductsBikeCount])
              }}</span>
            </trek-button>
            <!--  Clear all qaid logic  -->
            <trek-button
              tertiary
              block
              qaid="partTypeKey === 'bikes' || partTypeKey === 'projectOneBikes' ? 'clear-all-selected-bikes' : 'clear-all-selected-accessories'"
              :disabled="isDisabled"
              @click="clearSelectedProducts(partTypeKey)"
            >
              <span>{{ $t('text.account.savedLists.clear') }}</span>
            </trek-button>
          </div>
          <div ref="saved-lists-panel">
            <saved-lists-panel
              v-if="partTypeKey !== 'projectOneBikes'"
              id="saved-lists-panel"
              :part-type-key="partTypeKey"
              :text-max-chars="$t('text.buyingZone.B2B.max40Char')"
              :text-my-lists="$t('text.account.savedLists.myLists')"
              :text-other-lists="$t('text.account.savedLists.otherLists')"
              :text-moved-to-list="$t('text.account.savedLists.movedToList')"
              :text-undo="$t('text.undo')"
              :is-cart="true"
              qaid-panel="saved-lists-panel"
              qaid-title="saved-lists-panel-title"
              qaid-list="saved-lists-panel-list"
              qaid-list-item="saved-lists-panel-list-item"
              :btn-id="partTypeKey === 'bikes' ? 'saved-lists-button-bikes' : 'saved-lists-button-accessories'"
              @submit="removeProducts"
            >
            </saved-lists-panel>
          </div>
          <pdl-dialog
            v-model:visible="isDialogVisible"
            :title="$t('myCart.B2B.remove.confirm.multi')"
            :close-on-press-escape="false"
            :close-on-click-modal="false"
            :show-close="false"
            :z-index="16000011"
            qaid="delete-multiple-items-dialog"
            @close="resetDeleteData"
          >
            <template #footer>
              <div class="buttons buttons--right">
                <trek-button alert qaid="confirm-delete-all-selected" @click="confirmDelete">
                  <span>{{ $t('myCart.B2B.remove') }}</span>
                </trek-button>
                <trek-button secondary qaid="cancel-delete-all-selected" @click="isDialogVisible = false">
                  <span>{{ $t('myCart.B2B.doNotRemove.multi') }}</span>
                </trek-button>
              </div>
            </template>
          </pdl-dialog>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import pagination from '@/mixins/pagination';
import isEmpty from 'lodash/isEmpty';
import reduce from 'lodash/reduce';
import {mapActions, mapMutations, mapState, mapGetters} from 'vuex';
import swatchMixin from '@/mixins/swatches.js';
import {formatAppleData} from '@/utils/product-info';
import {ProductImages} from '@/components/containers/pdp/utils/product-images';
import TrekButton from '@/components/TrekButton.vue';
import {PdlFormGroup} from '@pedal/pdl-form-group';
import {PdlSwatch} from '@pedal/pdl-swatch';
import CartProductGrid from '@/components/cart/CartProductGrid.vue';
import {PdlTabs, PdlTabPane} from '@pedal/pdl-tabs';
import {PdlCollapse, PdlCollapseItem} from '@pedal/pdl-collapse';
import {PdlCallout} from '@pedal/pdl-callout';
import {PdlDialog} from '@pedal/pdl-dialog';
import SavedListsPanel from '@/components/saved-lists/SavedListsPanel.vue';
import PaginationControls from '@/components/PaginationControls.vue';
import AppleLabelGrid from '@/components/checkout/AppleLabelGrid.vue';

export default {
  components: {
    TrekButton,
    PdlFormGroup,
    PdlSwatch,
    CartProductGrid,
    PdlTabs,
    PdlTabPane,
    PdlCollapse,
    PdlCollapseItem,
    PdlCallout,
    PdlDialog,
    SavedListsPanel,
    PaginationControls,
    AppleLabelGrid,
  },
  mixins: [pagination, swatchMixin],

  props: {
    listData: {
      type: Array,
      default: () => [],
    },
    partTypeKey: {
      type: String,
      default: '',
    },
    itemIdKey: {
      type: String,
      default: '',
    },
    cartSummary: {
      type: Object,
      default: () => null,
    },
    size: {
      type: Number,
      required: false,
      default: ACC.cartItemsPageSize || 15,
    },
  },

  data() {
    return {
      isDialogVisible: false,
      isAllChecked: false,
    };
  },

  computed: {
    ...mapState('backend', ['cartToggle', 'appleLabelAccess', 'isConsumerFriendlyMode']),
    ...mapGetters('cart', ['selectedProductsBikeCount', 'selectedProductsAccessoriesCount', 'selectedProducts']),

    isDisabled() {
      return (
        (this.partTypeKey === 'bikes' && this.selectedProductsBikeCount === 0) ||
        (this.partTypeKey === 'projectOneBikes' && this.selectedProductsBikeCount === 0) ||
        (this.partTypeKey !== 'bikes' &&
          this.partTypeKey !== 'projectOneBikes' &&
          this.selectedProductsAccessoriesCount === 0) ||
        false
      );
    },
    collapseRef() {
      return `collapse-${this.partTypeKey}`;
    },
    checkboxId() {
      return `cart-checkbox-check-all-${this.partTypeKey}`;
    },
    checkboxValue() {
      return `CheckAll${this.partTypeKey}`;
    },
    isCustomWaterBottle() {
      return this.partTypeKey === 'customWaterBottles';
    },
    isProjectOne() {
      return this.partTypeKey === 'projectOneBikes';
    },
  },
  watch: {
    selectedProducts() {
      this.isAllChecked = this.getEntries().every(
        (entry) => !!this.selectedProducts.find((product) => entry.cartEntryPk === product.cartEntryPk)
      );
    },
  },
  methods: {
    resetDeleteData() {
      this.isDialogVisible = false;
    },
    confirmDelete() {
      this.deleteSelectedProducts(this.partTypeKey);
      this.isDialogVisible = false;
    },
    getAppleLabelData(item) {
      if (!this.displayAppleLabels) return [];

      return formatAppleData(item.entries);
    },

    updateSavedList() {
      if (this.partTypeKey === 'bikes') {
        this.setEntries(this.selectedProducts.filter((product) => product.productType === 'bike'));
      } else {
        this.setEntries(this.selectedProducts.filter((product) => product.productType !== 'bike'));
      }
    },

    getProductQuantity(product) {
      return reduce(product.warehouses, (result, warehouse) => result + parseInt(warehouse.orderedQty), 0);
    },

    displayAppleLabels(partTypeKey) {
      return this.appleLabelAccess[partTypeKey] && !isEmpty(this.listData[0].entries[0].appleLabels);
    },

    getPartKeyQAID(name) {
      return `cart.${this.partTypeKey}.tab.${name}`;
    },

    getGroupDisplayImage(product) {
      const transformations = {
        fl: 'strip_profile',
        w: 160,
        h: 120,
        c: 'pad',
      };

      const image = product?.entries?.[0]?.variantImage;

      if (this.isProjectOne) {
        return image ? image : null;
      } else {
        return image ? ProductImages.getAssetUrl(image, transformations) : null;
      }
    },
    getGroupDisplayColor(product) {
      return product?.entries?.[0]?.variantValueCategories?.[0]?.Color?.code;
    },
    getColorProps(product) {
      const code = this.getGroupDisplayColor(product);

      return this.processColorCode(code);
    },

    getGroupDisplayColorName(product) {
      return product?.entries?.[0]?.variantValueCategories?.[0]?.Color?.name;
    },

    getGroupingVariant(product) {
      const variantValueCategory = product?.entries?.[0]?.variantValueCategories?.[0];
      return variantValueCategory ? variantValueCategory[product.groupBy] : null;
    },

    getCollapseItemName(partTypeKey, itemIdKey, index) {
      return `${partTypeKey}-${itemIdKey}-${index}`;
    },

    getProductGridKey(item, index) {
      return `grid-${this.partTypeKey}-${item.entries[0].cartEntryPk}-${index}`;
    },

    getEntries() {
      const entries = [];
      this.listData.forEach((product) => {
        product.entries.forEach((entry) => {
          entries.push(entry);
        });
      });
      return entries;
    },

    updatePage(page) {
      if (page !== this.pageNumber) {
        this.pageNumber = page;
      }

      this.$nextTick(() => {
        if (this.$refs[this.collapseRef] !== null) {
          this.$refs[this.collapseRef][0].setAllActive();
        }
      });
    },

    checkAllItems(e) {
      this.isAllChecked = e.target.checked;
      if (this.isAllChecked) {
        this.getEntries().forEach((entry) => {
          this.addSelectedProduct({
            sku: entry.sku,
            qty: this.getProductQuantity(entry),
            productType: entry.productType,
            lineNote: entry.lineNote,
            productRecipeID: entry.productRecipeID,
            cartEntryPk: entry.cartEntryPk,
          });
        });
      } else {
        this.clearSelectedProducts(this.partTypeKey);
      }
    },

    getSavedListsPanelElement() {
      return this.$refs['saved-lists-panel'];
    },

    ...mapMutations('savedLists', ['setEntries']),

    ...mapActions('cart', [
      'removeProducts',
      'undoRemove',
      'clearSelectedProducts',
      'updateCart',
      'deleteSelectedProducts',
      'addSelectedProduct',
    ]),
  },
};
</script>
